import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, ArrowLeft, Check, ExternalLink, X, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { FaqSection } from "../components/FaqSection";
import { getProduct, PRODUCTS } from "../lib/products";

const ProductGallery = ({ product }) => {
    const shots = product.gallery || [];
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);

    // Close helper that doesn't directly mutate history — used by both
    // explicit close actions and the popstate listener below.
    const closeLightbox = React.useCallback(() => setOpen(false), []);

    // Open the lightbox + push a history entry so the browser back-button
    // closes the lightbox rather than navigating away from the product page.
    const openAt = React.useCallback((i) => {
        setIdx(i);
        setOpen(true);
        try {
            window.history.pushState(
                { scitusLightbox: true, productSlug: product.slug },
                "",
                window.location.href,
            );
        } catch (_) { /* history APIs may be restricted in some sandboxed previews */ }
    }, [product.slug]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowRight") setIdx((i) => (i + 1) % shots.length);
            else if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + shots.length) % shots.length);
        };
        // Browser back button → close lightbox instead of leaving the page.
        const onPop = () => closeLightbox();
        document.addEventListener("keydown", onKey);
        window.addEventListener("popstate", onPop);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("popstate", onPop);
            document.body.style.overflow = "";
        };
    }, [open, shots.length, closeLightbox]);

    // When the user clicks the in-page close (X / backdrop), also unwind the
    // history entry we pushed when opening so a subsequent forward/back
    // doesn't re-open the lightbox.
    const userClose = () => {
        if (window.history.state && window.history.state.scitusLightbox) {
            try { window.history.back(); return; } catch (_) { /* fall through */ }
        }
        closeLightbox();
    };

    if (!shots.length) return null;

    return (
        <section
            className="section product-gallery-section"
            data-testid={`product-gallery-${product.slug}`}
        >
            <div className="container">
                <div className="section-head left">
                    <span className="eyebrow">Inside {product.name}</span>
                    <h2 className="h2" style={{ marginTop: 18 }}>
                        Real screens. Live data. No mockups.
                    </h2>
                    <p>Every screen below is a live product capture. Click any frame to zoom.</p>
                </div>
            </div>

            <div className="gallery-masonry" data-testid={`gallery-rail-${product.slug}`}>
                {shots.map((src, i) => (
                    <button
                        key={src}
                        type="button"
                        className="gallery-tile"
                        onClick={() => openAt(i)}
                        data-testid={`gallery-tile-${product.slug}-${i + 1}`}
                        aria-label={`Open ${product.name} screenshot ${i + 1}`}
                    >
                        <img
                            src={src}
                            alt={`${product.name} screenshot ${i + 1}`}
                            loading="lazy"
                            decoding="async"
                            width="1600"
                            height="1000"
                        />
                        <span className="gallery-tile-num">0{i + 1}</span>
                    </button>
                ))}
            </div>

            {open && typeof document !== "undefined" && createPortal((
                <div
                    className="gallery-lightbox"
                    data-testid={`gallery-lightbox-${product.slug}`}
                    onClick={userClose}
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${product.name} screenshot viewer`}
                >
                    <button type="button" className="gallery-lb-close" onClick={(e) => { e.stopPropagation(); userClose(); }} aria-label="Close">
                        <X size={22} />
                    </button>
                    <button
                        type="button"
                        className="gallery-lb-nav prev"
                        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + shots.length) % shots.length); }}
                        aria-label="Previous"
                    >
                        <ChevronLeft size={28} />
                    </button>
                    <div className="gallery-lb-stage" onClick={(e) => e.stopPropagation()}>
                        <img
                            key={shots[idx]}
                            src={shots[idx]}
                            alt={`${product.name} screenshot ${idx + 1}`}
                            className="gallery-lb-img"
                            loading="eager"
                            decoding="async"
                        />
                    </div>
                    <button
                        type="button"
                        className="gallery-lb-nav next"
                        onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % shots.length); }}
                        aria-label="Next"
                    >
                        <ChevronRight size={28} />
                    </button>
                    <div className="gallery-lb-count">{idx + 1} / {shots.length}</div>
                </div>
            ), document.body)}
        </section>
    );
};

export const ProductDetailPage = ({ slug }) => {
    const p = getProduct(slug);
    if (!p) {
        return (
            <section className="section">
                <div className="container">
                    <p className="muted">Unknown product. <a href="/products" className="link-arrow">Back to products →</a></p>
                </div>
            </section>
        );
    }
    const Icon = p.icon;
    const hasLockup = p.logo && p.logo.src && p.logo.fit === "lockup";
    const hasMark = p.logo && p.logo.src && p.logo.fit === "mark";
    const others = PRODUCTS.filter((x) => x.slug !== p.slug).slice(0, 3);

    return (
        <>
            {/* HERO */}
            <section className="section product-hero" data-testid={`product-detail-${p.slug}`}>
                <div className="container">
                    <a href="/products" className="link-arrow" data-testid="product-back-link" style={{ marginBottom: 18 }}>
                        <ArrowLeft size={14} /> All products
                    </a>
                    <FadeUpGroup>
                        <div className="product-hero-grid fade-up">
                            <div>
                                {hasLockup ? (
                                    <div className="product-lockup-wrap" data-testid={`product-lockup-${p.slug}`}>
                                        <img
                                            src={p.logo.src}
                                            alt={`${p.name} logo`}
                                            className="product-lockup"
                                        />
                                        {/* Semantic <h1> for crawlers — Google, GPTBot, ClaudeBot,
                                            PerplexityBot. Visually hidden because the lockup image
                                            already serves as the visible page heading. */}
                                        <h1 className="sr-only">{p.name}</h1>
                                        <div className="sub" style={{ marginTop: 14 }}>{p.sub}</div>
                                    </div>
                                ) : (
                                    <div className="product-head">
                                        <span className={`icon-big${hasMark && p.logo?.needsLightTile ? " icon-big--lit" : ""}`}>
                                            {hasMark ? (
                                                <img
                                                    src={p.logo.src}
                                                    alt={`${p.name} mark`}
                                                    style={{ width: 96, height: 96, objectFit: "contain" }}
                                                    width="96"
                                                    height="96"
                                                    loading="eager"
                                                    decoding="async"
                                                />
                                            ) : (
                                                <Icon size={32} />
                                            )}
                                        </span>
                                        <div>
                                            <div className="sub">{p.sub}</div>
                                            <h1 className="h1" style={{ fontSize: "clamp(2.25rem, 4vw, 3.25rem)", marginTop: 4 }}>{p.name}</h1>
                                        </div>
                                    </div>
                                )}
                                <p className="lead" style={{ marginTop: 22, maxWidth: 560 }}>{p.description}</p>
                                <div className="hero-cta-row">
                                    <a href="/contact" className="btn btn-primary btn-lg" data-testid={`product-cta-demo-${p.slug}`}>
                                        Book a {p.name} demo <ArrowRight size={16} />
                                    </a>
                                    <a href="/pricing" className="btn btn-ghost btn-lg" data-testid={`product-cta-pricing-${p.slug}`}>
                                        See pricing
                                    </a>
                                    {p.launchUrl && (
                                        <a
                                            href={p.launchUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-launch btn-lg"
                                            data-testid={`product-cta-launch-${p.slug}`}
                                        >
                                            {p.launchLabel || `Launch ${p.name}`} <ExternalLink size={14} />
                                        </a>
                                    )}
                                </div>
                                <div className="industry-chips" style={{ marginTop: 28 }}>
                                    {p.industries.map((ind) => <span key={ind} className="chip">{ind}</span>)}
                                </div>
                            </div>
                            {p.heroShot ? (
                                <div
                                    className="product-hero-shot"
                                    data-testid={`product-hero-shot-${p.slug}`}
                                >
                                    <img
                                        src={p.heroShot}
                                        alt={`${p.name} — live product screenshot`}
                                        loading="eager"
                                        fetchpriority="high"
                                        decoding="async"
                                        width="1600"
                                        height="1000"
                                    />
                                </div>
                            ) : (
                                <div className="product-mockup" style={{ minHeight: 340 }}>
                                    <div className="mockup-bg-grid" />
                                    <div className="mockup-content">
                                        <div className="sub" style={{ marginBottom: 14 }}>{p.name} · Preview</div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                            {p.pillars.map((pi) => (
                                                <div key={pi.h} style={{ padding: 14, background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--border)" }}>
                                                    <span className="icon-square" style={{ width: 32, height: 32, marginBottom: 8 }}><pi.icon size={16} /></span>
                                                    <div style={{ fontFamily: "'Cabinet Grotesk'", fontWeight: 700, fontSize: "0.95rem", color: "var(--text)" }}>{pi.h}</div>
                                                    <div style={{ color: "var(--text-muted)", fontSize: "0.78rem", marginTop: 2 }}>{pi.p}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* PILLARS */}
            <section className="section" style={{ background: "var(--surface)", paddingTop: 60, paddingBottom: 60 }}>
                <div className="container">
                    <FadeUpGroup>
                        <div className="pillar-grid">
                            {p.pillars.map((pi) => (
                                <div key={pi.h} className="card fade-up" data-testid={`pillar-${pi.h.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <span className="icon-square"><pi.icon size={22} /></span>
                                    <h3 className="h3" style={{ marginTop: 16 }}>{pi.h}</h3>
                                    <p className="muted" style={{ marginTop: 8 }}>{pi.p}</p>
                                </div>
                            ))}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* DETAILED FEATURES */}
            <section className="section">
                <div className="container">
                    <div className="section-head left">
                        <span className="eyebrow">What you get</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>{p.name}, end-to-end</h2>
                    </div>
                    <FadeUpGroup>
                        <div className="feature-themes" data-testid="feature-themes">
                            {Object.entries(p.features).map(([theme, list]) => (
                                <div key={theme} className="feature-theme fade-up" data-testid={`feature-theme-${theme.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <h3 className="h3" style={{ fontSize: "1.05rem", color: "var(--accent)" }}>{theme}</h3>
                                    <ul className="feature-list" style={{ marginTop: 14 }}>
                                        {list.map((f) => (
                                            <li key={f}>
                                                <span className="check"><Check size={14} /></span>{f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* GALLERY — real product screenshots */}
            <ProductGallery product={p} />

            {/* HOW IT WORKS */}
            <section className="section" style={{ background: "var(--surface)" }}>
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">How it works</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>From signed contract to live in weeks</h2>
                    </div>
                    <FadeUpGroup>
                        <div className="steps-grid" data-testid="steps-grid">
                            {p.howItWorks.map((s, i) => (
                                <div key={s.h} className="step-card fade-up">
                                    <div className="step-num">Step 0{i + 1}</div>
                                    <h3>{s.h}</h3>
                                    <p>{s.p}</p>
                                </div>
                            ))}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* USE CASES */}
            <section className="section">
                <div className="container">
                    <div className="section-head">
                        <span className="eyebrow">Use cases</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>How operators actually use {p.name}</h2>
                    </div>
                    <FadeUpGroup>
                        <div className="usecase-grid" data-testid="usecase-grid">
                            {p.useCases.map((u) => (
                                <div key={u.h} className="card fade-up" data-testid={`usecase-${u.ind.toLowerCase()}`}>
                                    <span className="eyebrow" style={{ padding: "4px 10px" }}>{u.ind}</span>
                                    <h3 className="h3" style={{ marginTop: 12 }}>{u.h}</h3>
                                    <p className="muted" style={{ marginTop: 8 }}>{u.p}</p>
                                </div>
                            ))}
                        </div>
                    </FadeUpGroup>
                </div>
            </section>

            {/* OPTIONAL: LIVE DEMO CALLOUT (per-product) */}
            {p.demo && p.demo.available && (
                <section
                    className="section"
                    style={{ background: "var(--surface)" }}
                    data-testid={`product-demo-${p.slug}`}
                >
                    <div className="container">
                        <div className="section-head left">
                            <span className="eyebrow">Live demo</span>
                            <h2 className="h2" style={{ marginTop: 18 }}>
                                Walk through {p.name} with role-based personas
                            </h2>
                            <p>{p.demo.note}</p>
                        </div>
                        <FadeUpGroup>
                            <div className="industry-chips" style={{ marginTop: 8, marginBottom: 24 }}>
                                {p.demo.personas.map((persona) => (
                                    <span
                                        key={persona}
                                        className="chip"
                                        data-testid={`product-demo-persona-${persona.toLowerCase().replace(/\s+/g, "-")}`}
                                    >
                                        {persona}
                                    </span>
                                ))}
                            </div>
                            <a
                                href="/contact"
                                className="btn btn-primary btn-lg"
                                data-testid={`product-demo-request-${p.slug}`}
                            >
                                Request demo access <ArrowRight size={16} />
                            </a>
                        </FadeUpGroup>
                    </div>
                </section>
            )}

            {/* FAQ */}
            <FaqSection
                heading={`${p.name} — frequently asked`}
                items={p.faqs}
                testIdPrefix={`faq-${p.slug}`}
            />

            {/* OTHER PRODUCTS */}
            <section className="section" style={{ background: "var(--surface)" }}>
                <div className="container">
                    <div className="section-head left">
                        <span className="eyebrow">More from Scitus</span>
                        <h2 className="h2" style={{ marginTop: 18 }}>{p.name} works even better with these</h2>
                    </div>
                    <div className="product-strip" style={{ gridTemplateColumns: "" }}>
                        {others.map((o) => (
                            <a key={o.slug} href={`/products/${o.slug}`} className="product-card" data-testid={`related-product-${o.slug}`}>
                                <span className="icon-pill"><o.icon size={20} /></span>
                                <span className="sub">{o.sub}</span>
                                <span className="name">{o.name}</span>
                                <p className="desc">{o.tagline}</p>
                                <span className="link-arrow">Explore <ArrowRight size={16} /></span>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section" style={{ paddingTop: 0 }}>
                <div className="container">
                    <div className="cta-banner">
                        <div>
                            <h2 className="h2">See {p.name} on your data.</h2>
                            <p className="lead mt-4">A 30-minute demo, tailored to your industry and team size.</p>
                        </div>
                        <a href="/contact" className="btn btn-primary btn-lg" data-testid={`product-cta-bottom-${p.slug}`}>
                            Book a Demo <ArrowRight size={18} />
                        </a>
                    </div>
                </div>
            </section>
        </>
    );
};

export default ProductDetailPage;
;
